/**
 * Backup Synchronization Utilities
 *
 * Portable writes save matching session/local snapshots with best-effort rollback.
 * Legacy callers without a password may explicitly retain session-only changes.
 *
 * IMPORTANT: Always use these functions instead of directly updating storage
 *
 * ============================================================================
 * BACKUP ENCRYPTION ARCHITECTURE
 * ============================================================================
 *
 * LOCAL STORAGE (STORAGE_KEYS.PRF_ENCRYPTED_BACKUP):
 *   - Encrypted with WebAuthn PRF-derived key (biometric unlock)
 *   - Fast, seamless UX - user touches Face ID / Touch ID
 *   - DEVICE-BOUND: Unrecoverable without the passkey on this device
 *   - Used for daily sign-in convenience
 *
 * PASSWORD-ENCRYPTED (STORAGE_KEYS.ENCRYPTED_BACKUP & exported files):
 *   - Encrypted with user's password via bitcoin-backup (PBKDF2-SHA256)
 *   - Slower UX but portable and recoverable
 *   - PORTABLE: Can be decrypted on any device with the password
 *   - Used for: exported backups, cloud sync, device recovery
 *
 * ⚠️  CRITICAL RULES:
 *   1. NEVER export PRF-encrypted backups - they're unrecoverable on other devices
 *   2. If user deletes passkey, PRF-encrypted local backup is LOST
 *   3. Downloaded/exported backups MUST always use password encryption
 *   4. Users should always have a password-encrypted backup as fallback
 *
 * @see lib/webauthn/encryption.ts for PRF encryption implementation
 * @see downloadEncryptedBackup() in utils.ts for export (always uses password)
 * ============================================================================
 */

import type { BapMasterBackup } from "bitcoin-backup";
import { isWifBackup } from "bitcoin-backup";
import { STORAGE_KEYS } from "../storage-keys";
import { invalidateStalePrfState } from "../webauthn/prf-invalidate";
import type { CloudBackupRefreshResult } from "./cloud-refresh";
import { getBAPInstance } from "./core";
import {
	commitPortableBackup,
	refreshCommittedCloudBackup,
} from "./durable-backup";
import {
	encryptIdentityBackup,
	type IdentityBackup,
	isSeedIdentityBackup,
	validateIdentityBackup,
} from "./identity-backup";
import { SeedProfileAdapter } from "./seed-profile-adapter";

/**
 * Save matching decrypted/session and encrypted/portable backup snapshots.
 * Storage rollback is best effort; browser storage offers no cross-store transaction.
 * This is the ONLY function that should update backups in storage
 *
 * Updates:
 * 1. sessionStorage (decrypted) - always
 * 2. localStorage password-encrypted backup - if password provided
 * 3. localStorage PRF-encrypted backup - if user has PRF credential (requires re-auth)
 *
 * Note: PRF re-encryption requires a separate WebAuthn authentication, so the PRF
 * backup may become stale after changes. Users should re-setup biometric unlock
 * after significant changes, or rely on password backup as primary.
 *
 * @param updatedBackup - The updated backup to save
 * @param password - Password for encryption (optional, will try to get from existing)
 * @returns Commit status, portable persistence status, and separate cloud status
 */
export async function syncBackupToStorage(
	updatedBackup: IdentityBackup,
	password?: string,
	options?: { deferCloudFlush?: boolean; requirePortable?: boolean }
): Promise<{
	success: boolean;
	portablePersisted: boolean;
	error?: string;
	rollbackFailed?: boolean;
	cloud?: CloudBackupRefreshResult | { status: "deferred" };
	prfInvalidationFailed?: boolean;
}> {
	if (options?.requirePortable && (typeof password !== "string" || !password)) {
		return {
			success: false,
			portablePersisted: false,
			error: "A backup password is required",
		};
	}
	let committedEncrypted: string;
	try {
		const serialized = JSON.stringify(validateIdentityBackup(updatedBackup));
		if (!password) {
			// Existing callers may deliberately keep a session-only update.
			sessionStorage.setItem(STORAGE_KEYS.DECRYPTED_BACKUP, serialized);
			notifyBackupUpdated();
			if (localStorage.getItem(STORAGE_KEYS.PRF_ENCRYPTED_BACKUP)) {
				await invalidateStalePrfState();
			}
			return { success: true, portablePersisted: false };
		}
		const previousEncrypted = localStorage.getItem(
			STORAGE_KEYS.ENCRYPTED_BACKUP
		);
		const previousDecrypted = sessionStorage.getItem(
			STORAGE_KEYS.DECRYPTED_BACKUP
		);
		// Encrypt the exact serialized snapshot, even if the caller later mutates its object.
		const snapshot = validateIdentityBackup(JSON.parse(serialized));
		const encrypted = await encryptIdentityBackup(snapshot, password);
		if (
			localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP) !==
				previousEncrypted ||
			sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP) !==
				previousDecrypted
		) {
			return {
				success: false,
				portablePersisted: false,
				error: "Backup save was superseded by another update",
			};
		}
		committedEncrypted = encrypted;
		const committed = commitPortableBackup(
			serialized,
			encrypted,
			localStorage,
			sessionStorage
		);
		if (!committed.success) {
			return committed;
		}
	} catch {
		return {
			success: false,
			portablePersisted: false,
			error: "Failed to synchronize backup",
		};
	}

	// Portable and session copies now match. Follow-up failures cannot undo that success.
	let prfInvalidationFailed = false;
	try {
		if (localStorage.getItem(STORAGE_KEYS.PRF_ENCRYPTED_BACKUP)) {
			await invalidateStalePrfState();
		}
	} catch {
		prfInvalidationFailed = true;
	}
	try {
		notifyBackupUpdated();
	} catch {
		// An event listener failure does not invalidate the saved portable backup.
	}
	const cloud = await refreshCommittedCloudBackup(
		committedEncrypted,
		options?.deferCloudFlush
	);
	return {
		success: true,
		portablePersisted: true,
		cloud,
		...(prfInvalidationFailed ? { prfInvalidationFailed: true } : {}),
	};
}

/**
 * Get the decrypted backup from sessionStorage
 * This is the source of truth during an active session
 */
export function getDecryptedBackup(): IdentityBackup | null {
	try {
		const backupJson = sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP);
		if (!backupJson) {
			return null;
		}
		return validateIdentityBackup(JSON.parse(backupJson));
	} catch {
		return null;
	}
}

export type BapIdRemovalPreflight =
	| { success: true }
	| { success: false; error: string };

/** Verify local removability without mutating the backup or BAP instance. */
export function preflightBapIdRemoval(bapId: string): BapIdRemovalPreflight {
	const backup = getDecryptedBackup();

	if (!backup) {
		return { success: false, error: "No backup found in session" };
	}
	if (isSeedIdentityBackup(backup)) {
		if (backup.inventoryComplete === false) {
			return {
				success: false,
				error: "Import your complete backup file before removing a profile.",
			};
		}
		if (backup.profiles.length <= 1) {
			return { success: false, error: "Cannot remove the final seed profile" };
		}
		return backup.profiles.some((profile) => profile.bapId === bapId)
			? { success: true }
			: { success: false, error: "Identity not found in backup" };
	}

	// A WIF represents one identity and has no removable BAP ID collection.
	if (isWifBackup(backup)) {
		return {
			success: false,
			error: "Cannot remove the identity from a WIF backup",
		};
	}

	const bap = getBAPInstance();
	if (!bap) {
		return { success: false, error: "Failed to initialize BAP" };
	}

	if (!bap.getId(bapId)) {
		return { success: false, error: "Identity not found in backup" };
	}

	return { success: true };
}

/**
 * Update BAP IDs in backup and sync to storage
 * This should be called whenever newId() is used or IDs are modified
 *
 * @param updatedIds - The new serialized IDs string from bap.exportIds()
 * @param password - Optional password for encryption
 */
export function updateBapIdsInBackup(
	updatedIds: string,
	password?: string
): Promise<{ success: boolean; error?: string }> {
	const backup = getDecryptedBackup();

	if (!backup) {
		return Promise.resolve({
			success: false,
			error: "No backup found in session",
		});
	}

	// WIF backups don't have IDs to update
	// Note: Type42 (rootPk) backups DO have ids and must be updated
	if (isWifBackup(backup)) {
		return Promise.resolve({ success: true });
	}
	if (isSeedIdentityBackup(backup)) {
		return Promise.resolve({
			success: false,
			error: "Seed inventories must be saved in their versioned format",
		});
	}

	// Update the IDs
	const updatedBackup: BapMasterBackup = {
		...backup,
		ids: updatedIds,
	};

	return syncBackupToStorage(updatedBackup, password);
}

/**
 * Remove a BAP ID from the backup file
 * Used when discarding an unpublished identity
 *
 * @param bapId - The BAP ID (identity key) to remove
 * @param password - Password for re-encrypting backup (optional)
 * @returns Success status with error message if failed
 */
export function removeBapIdFromBackup(
	bapId: string,
	password?: string
): Promise<{ success: boolean; error?: string }> {
	const preflight = preflightBapIdRemoval(bapId);
	if (!preflight.success) {
		return Promise.resolve(preflight);
	}
	const backup = getDecryptedBackup();
	if (isSeedIdentityBackup(backup)) {
		const adapter = new SeedProfileAdapter(backup);
		adapter.removeId(bapId);
		return syncBackupToStorage(adapter.exportBackup(), password, {
			requirePortable: true,
		});
	}

	const bap = getBAPInstance();
	// Keep a defensive guard in case session storage changes after preflight.
	if (!bap) {
		return Promise.resolve({
			success: false,
			error: "Failed to initialize BAP",
		});
	}

	// Remove the ID from the BAP instance
	bap.removeId(bapId);

	// Get the updated IDs after removal
	const updatedIds = bap.exportIds();

	// Update the backup with new IDs
	return updateBapIdsInBackup(updatedIds, password);
}

/** localStorage key for a BAP draft profile */
function draftProfileKey(bapId: string): string {
	return `bap-draft-profile-${bapId}`;
}

/**
 * Read the raw local draft profile string for an identity.
 * Returns the raw string (stable reference for useSyncExternalStore).
 */
export function readLocalDraftProfile(bapId: string): string | null {
	return localStorage.getItem(draftProfileKey(bapId));
}

/**
 * Write (or clear, with null) the local draft profile copy and notify
 * listeners. This is the ONLY place the local draft cache should be
 * mutated so that every subscriber sees the change.
 */
export function writeLocalDraftProfile(
	bapId: string,
	profileData: Record<string, unknown> | null
): void {
	if (profileData) {
		localStorage.setItem(draftProfileKey(bapId), JSON.stringify(profileData));
	} else {
		localStorage.removeItem(draftProfileKey(bapId));
	}
	// StorageEvent doesn't fire in the same window, so we use a custom event
	window.dispatchEvent(new Event("bap-draft-updated"));
}

/**
 * Atomically save draft profile to:
 * 1. Database (KV) for persistence across sessions
 * 2. localStorage for immediate local access
 * 3. Trigger storage event to update identity context
 *
 * This is the ONLY function that should be used to save profile drafts
 *
 * @param bapId - The BAP ID to update
 * @param profileData - The complete profile object to save
 */
export async function saveDraftProfile(
	bapId: string,
	profileData: Record<string, unknown>
): Promise<{ success: boolean; error?: string }> {
	try {
		// 1. Save draft to database (KV) via API
		const response = await fetch("/api/bap/profile", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({
				bapId,
				profile: profileData,
			}),
		});

		if (!response.ok) {
			throw new Error("Failed to save draft");
		}

		// 2. Update local cache (localStorage) and notify listeners
		writeLocalDraftProfile(bapId, profileData);

		return { success: true };
	} catch {
		return {
			success: false,
			error: "Failed to save draft profile",
		};
	}
}

/**
 * Dispatch an event when backup is updated
 * Other components can listen to this to stay in sync
 */
export function notifyBackupUpdated(): void {
	window.dispatchEvent(new Event("bap-backup-updated"));
}

/**
 * Calculate SHA-256 hash of encrypted backup
 */
async function calculateBackupHash(encryptedBackup: string): Promise<string> {
	const encoder = new TextEncoder();
	const data = encoder.encode(encryptedBackup);
	const hashBuffer = await crypto.subtle.digest("SHA-256", data);
	const hashArray = Array.from(new Uint8Array(hashBuffer));
	const hashHex = hashArray
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");
	return hashHex;
}

/**
 * Get current encrypted backup hash
 */
export function getCurrentBackupHash(): Promise<string | null> {
	const encryptedBackup = localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP);
	if (!encryptedBackup) {
		return Promise.resolve(null);
	}
	return calculateBackupHash(encryptedBackup);
}

/**
 * Get the hash of the last downloaded backup
 */
export function getLastDownloadedBackupHash(): string | null {
	return localStorage.getItem("sigma-last-downloaded-backup-hash");
}

/**
 * Mark current backup as downloaded (store hash)
 */
export async function markBackupAsDownloaded(): Promise<void> {
	const currentHash = await getCurrentBackupHash();
	if (currentHash) {
		localStorage.setItem("sigma-last-downloaded-backup-hash", currentHash);
		// Dispatch event to dismiss download toast
		window.dispatchEvent(new Event("backup-downloaded"));
	}
}

/**
 * Check if backup needs to be downloaded (hash mismatch)
 */
export async function needsBackupDownload(): Promise<boolean> {
	const currentHash = await getCurrentBackupHash();
	const lastDownloadedHash = getLastDownloadedBackupHash();

	if (!currentHash) {
		return false; // No backup to download
	}

	if (!lastDownloadedHash) {
		return true; // Never downloaded before
	}

	return currentHash !== lastDownloadedHash;
}

/**
 * Download the current encrypted backup as a .json file
 */
export async function downloadBackupFile(): Promise<void> {
	const encryptedBackup = localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP);
	if (!encryptedBackup) {
		return;
	}

	const filename = `sigma-backup-${new Date().toISOString().split("T")[0]}.json`;
	const blob = new Blob([encryptedBackup], { type: "application/json" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);

	// Mark as downloaded (await to ensure hash is saved before function returns)
	await markBackupAsDownloaded();
}
